import { Location, NgClass, UpperCasePipe } from '@angular/common';
import { AfterViewInit, Component, computed, DestroyRef, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { AppConfig } from '@app/app.config';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { environment } from '@env/environment';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
    LoginResponse,
    Organization,
    UserRoles,
} from 'api/models';
import {
    AppStore,
} from '@app/stores/appStore';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { UserProfileComponent } from '../user-profile/user-profile.component';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    imports: [
        NgClass,
        RouterLink,
        MatMenuTrigger,
        MatTooltip,
        MatIcon,
        MatMenu,
        MatMenuItem,
        UserProfileComponent,
        MatButton,
        UpperCasePipe,
        TranslateModule,
    ],
})
export class HeaderComponent implements OnInit, AfterViewInit {
    private appStore = inject(AppStore);
    userSession = signal<LoginResponse | null>(null);
    isExternal = signal(false);
    isLegal = signal(false);
    showHeader: boolean = false;
    organization: Organization;
    availableLanguages = environment.availableLanguages.filter(
        lang => lang.code === 'de' || lang.code === 'en' || lang.code === 'es'
    );
    currentLanguage = signal('');
    organisationName = signal('');

    // Store signal reads — used in template as isLoggedIn(), currentSiteRole(), etc.
    isLoggedIn = this.appStore.isLoggedIn;
    currentOrganization = this.appStore.currentOrganizationSite;
    currentSiteRole = this.appStore.currentSiteRole;

    constructor(
        private router: Router,
        public sessionService: SessionService,
        private storageService: StorageService,
        private translateService: TranslateService,
        public appConfig: AppConfig,
        public location: Location
    ) {
        const destroyRef = inject(DestroyRef);

        // Bridge SessionService signals → appStore (replaces subscribe bridges)
        effect(() => {
            const session = this.sessionService.userSessionSignal();
            this.userSession.set(session ? session : null);
            if (session?.organizations[0]?.name) {
                this.organisationName.set(session.organizations[0].name);
            }
            this.appStore.setIsLoggedIn(session ? true : false);
        });

        effect(() => {
            this.appStore.setCurrentOrganizationSite(this.sessionService.currentOrganizationSiteSignal());
        });

        effect(() => {
            this.appStore.setCurrentSiteRole(this.sessionService.currentSiteRole());
        });

        // Router.events subscribe — uses takeUntilDestroyed for lifecycle management
        this.router.events.pipe(takeUntilDestroyed(destroyRef)).subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.isExternal.set(
                    event.url.includes('/external') &&
                    !event.url.includes('/admin'));
                this.isLegal.set(event.url.includes('/legal'));
            }
        });
    }

    ngOnInit(): void {
        this.currentLanguage.set(this.translateService.currentLang);

        if (this.storageService.itemExists('organisationName')) {
            this.organisationName.set(
                this.storageService.getItemAsObject('organisationName'));
        }
    }

    ngAfterViewInit(): void {
        if (this.storageService.itemExists('language_code')) {
            this.changeLanguage(
                this.storageService.getItemAsObject('language_code')
            );
        }
    }

    changeLanguage(code: string) {
        this.translateService.use(code);
        this.currentLanguage.set(code);
        this.storageService.setItem('language_code', code);

        // Update store with selected language
        this.appStore.setSelectedLanguage(code);
    }

    canSelectAdminPanel = computed(() => {
        if (this.isExternal()) return false;
        const session = this.sessionService.userSessionSignal();
        if (!session) return false;
        const permissions = session.permissions;
        const roles = session.roles;
        return !!(
            permissions?.service_provider_organizations_create ||
            permissions?.service_provider_organizations_read ||
            permissions?.service_provider_organizations_update ||
            roles?.includes(UserRoles.SiteAdmin) ||
            roles?.includes(UserRoles.OrganizationAdmin)
        );
    });
}
